// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { Container, Header, Button, Table, Message, Loader, Dropdown, Label, Icon } from 'semantic-ui-react';
import { providerApi, type Provider } from '../api/providerApi';
import { policyTypeApi, type PolicyType } from '../api/policyTypeApi';
import { quoteTermApi, type QuoteTerm } from '../api/quoteTermApi';
import QuoteTermEditorModal from '../components/QuoteTermEditorModal';

/**
 * Platform-admin surface for PLATFORM-scope Quote Terms (provider rate cards).
 * Pick a provider, manage its default allocation-rates-and-charges cards.
 * See docs/specs/quotes-engine-prd.md §7.1.
 */
export default function QuoteTermsPage() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerId, setProviderId] = useState('');
  const [products, setProducts] = useState<PolicyType[]>([]);
  const [cards, setCards] = useState<QuoteTerm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<QuoteTerm | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  useEffect(() => {
    providerApi.getAll({ isActive: true, size: 1000 })
      .then((d) => setProviders(d.content))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load providers'));
  }, []);

  const loadForProvider = useCallback(async (pid: string) => {
    if (!pid) { setCards([]); setProducts([]); return; }
    setLoading(true);
    setError(null);
    try {
      const [cardList, productPage] = await Promise.all([
        quoteTermApi.list(pid),
        policyTypeApi.getAll({ providerId: pid, isActive: true, size: 1000 }),
      ]);
      setCards(cardList);
      setProducts(productPage.content);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load rate cards');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadForProvider(providerId); }, [providerId, loadForProvider]);

  const providerOptions = [
    { key: '', text: 'Select a provider…', value: '' },
    ...providers.map((p) => ({ key: p.id!, text: p.name, value: p.id! })),
  ];

  const handleSeedDefaults = async () => {
    setSeeding(true);
    setError(null);
    setSeedMsg(null);
    try {
      const r = await quoteTermApi.seedDefaults();
      setSeedMsg(
        `Seeded ${r.created} default rate card(s); ${r.skipped} already existed ` +
          `(${r.considered} pension/investment products considered).`,
      );
      if (providerId) await loadForProvider(providerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to seed defaults');
    } finally {
      setSeeding(false);
    }
  };

  const openAdd = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = async (termId: string) => {
    try {
      const full = await quoteTermApi.get(termId);
      setEditing(full);
      setEditorOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load card');
    }
  };

  const handleSave = async (body: QuoteTerm) => {
    if (editing?.id) {
      await quoteTermApi.update(editing.id, body);
    } else {
      await quoteTermApi.create(body);
    }
    setEditorOpen(false);
    await loadForProvider(providerId);
  };

  const toggleActive = async (card: QuoteTerm) => {
    if (!card.id) return;
    if (card.isActive) await quoteTermApi.deactivate(card.id);
    else await quoteTermApi.activate(card.id);
    await loadForProvider(providerId);
  };

  const remove = async (card: QuoteTerm) => {
    if (!card.id) return;
    if (!globalThis.confirm(`Delete the ${card.productName ?? 'rate card'} terms?`)) return;
    await quoteTermApi.remove(card.id);
    await loadForProvider(providerId);
  };

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as="h1">Quote Terms</Header>
      <p>Platform default rate cards (allocation rates &amp; charges) per provider and product.</p>

      <Button
        basic color="blue" onClick={handleSeedDefaults} loading={seeding}
        style={{ marginBottom: '1rem' }} data-testid="seed-defaults"
      >
        <Icon name="magic" /> Seed default rate cards
      </Button>
      {seedMsg && (
        <Message positive onDismiss={() => setSeedMsg(null)}><p>{seedMsg}</p></Message>
      )}

      <div style={{ marginBottom: '1rem' }} />

      <Dropdown
        selection search placeholder="Select a provider…"
        options={providerOptions} value={providerId}
        onChange={(_e, d) => setProviderId(d.value as string)}
        style={{ marginBottom: '1rem', minWidth: '20rem' }}
      />
      {providerId && (
        <Button primary floated="right" onClick={openAdd} disabled={products.length === 0}>
          <Icon name="plus" /> Add Terms &amp; Quotes
        </Button>
      )}

      {error && <Message negative><p>{error}</p></Message>}
      {loading && <Loader active inline="centered" />}

      {providerId && !loading && (
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Product</Table.HeaderCell>
              <Table.HeaderCell>Category</Table.HeaderCell>
              <Table.HeaderCell>Options</Table.HeaderCell>
              <Table.HeaderCell>Currency</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {cards.length === 0 && (
              <Table.Row><Table.Cell colSpan={6}>No rate cards yet for this provider.</Table.Cell></Table.Row>
            )}
            {cards.map((card) => (
              <Table.Row key={card.id}>
                <Table.Cell>{card.productName}</Table.Cell>
                <Table.Cell>{card.category}</Table.Cell>
                <Table.Cell>{card.options?.length ?? 0}</Table.Cell>
                <Table.Cell>{card.currencyCode}</Table.Cell>
                <Table.Cell>
                  <Label color={card.isActive ? 'green' : 'grey'}>
                    {card.isActive ? 'Active' : 'Inactive'}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  <Button.Group size="small">
                    <Button icon="edit" color="blue" onClick={() => openEdit(card.id!)} />
                    <Button icon={card.isActive ? 'pause' : 'play'} onClick={() => toggleActive(card)} />
                    <Button icon="trash" color="red" onClick={() => remove(card)} />
                  </Button.Group>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <QuoteTermEditorModal
        open={editorOpen}
        providerId={providerId}
        products={products}
        existing={editing}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />
    </Container>
  );
}
